/**
 * One question at a time.
 *
 * There is no back button here unless the section allows one, and that is a
 * property of the test rather than a styling choice — the server refuses an
 * out-of-order answer regardless of what this offers.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import type { ItemTimer } from '../../lib/telemetry';
import Countdown from './Countdown';
import type { Snapshot } from './types';

export default function QuestionScreen({
  snapshot,
  clockOffsetMs,
  onAnswer,
  onAdvance,
  onFinish,
  onBreak,
  onDeadline,
  timer,
  busy,
  notice,
}: {
  snapshot: Snapshot;
  clockOffsetMs: number;
  onAnswer: (value: string) => Promise<void>;
  onAdvance: () => Promise<void>;
  onFinish: () => Promise<void>;
  onBreak: () => void;
  /** A displayed countdown reaching zero asks the server what happened. */
  onDeadline: () => void;
  timer: ItemTimer;
  busy: boolean;
  notice: string | null;
}) {
  const section = snapshot.section!;
  const item = snapshot.item!;
  const view = item.view;

  const [value, setValue] = useState<string>(item.savedValue ?? '');
  const previousLength = useRef(value.length);
  const isLastItem = item.index === item.count - 1;
  const isLastSection = section.index === section.count - 1;
  const isEnd = isLastItem && isLastSection;

  // A new question means a fresh field and a fresh timer.
  useEffect(() => {
    setValue(item.savedValue ?? '');
    previousLength.current = (item.savedValue ?? '').length;
    timer.reset();
  }, [view.id, item.savedValue, timer]);

  const wordCount = useMemo(
    () => (value.trim() ? value.trim().split(/\s+/).length : 0),
    [value],
  );
  const overLimit = view.maxWords !== undefined && wordCount > view.maxWords;

  const registerInput = (next: string) => {
    // Counts the change in length, which approximates keystrokes without ever
    // recording one. The candidate agreed to exactly this and no more.
    timer.registerInput(next.length - previousLength.current);
    previousLength.current = next.length;
    setValue(next);
  };

  const submit = async () => {
    await onAnswer(value);
    if (isEnd) await onFinish();
    else await onAdvance();
  };

  const choose = async (optionId: string) => {
    timer.registerInput(1);
    setValue(optionId);
    await onAnswer(optionId);
    if (isEnd) await onFinish();
    else await onAdvance();
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-10 border-b border-ink/10 bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{section.title}</p>
            <p className="text-xs text-ink/50">
              Question {item.index + 1} of {item.count} · {view.points}{' '}
              {view.points === 1 ? 'point' : 'points'}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-5">
            {snapshot.break?.available && (
              <button
                type="button"
                onClick={onBreak}
                disabled={busy}
                title="Pause your test timer for a short break"
                className="inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/5 px-3 py-1.5 text-sm font-medium text-accent hover:bg-accent/10"
              >
                <span aria-hidden>❚❚</span> Take a break
              </button>
            )}
            {item.deadlineAt !== null && (
              <Countdown
                deadlineAt={item.deadlineAt}
                clockOffsetMs={clockOffsetMs}
                onExpire={onDeadline}
                label="Question"
              />
            )}
            <Countdown
              deadlineAt={section.deadlineAt}
              clockOffsetMs={clockOffsetMs}
              onExpire={onDeadline}
              label="Section"
            />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-8">
        {notice && (
          <p className="mb-4 rounded-md border border-warn/40 bg-warn/5 px-4 py-2 text-sm text-warn">
            {notice}
          </p>
        )}

        {/* Prompts are authored with meaningful line breaks — sequences, code. */}
        <p className="prompt-text text-base leading-relaxed">{view.prompt}</p>

        {view.media && (
          <div className="mt-5">
            {/* Assets come from the test's own configuration, never a remote URL:
                the Content-Security-Policy would block one anyway. */}
            <img
              src={view.media.src}
              alt={view.media.alt}
              className="max-w-full rounded-md border border-ink/10"
            />
          </div>
        )}

        {view.hint && <p className="mt-2 text-xs text-ink/50">{view.hint}</p>}

        <div className="mt-6">
          {view.options ? (
            <div className="grid gap-2.5">
              {view.options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  disabled={busy}
                  onClick={() => void choose(option.id)}
                  className={[
                    'flex items-start gap-3 rounded-md border px-4 py-3 text-left text-sm transition-colors',
                    value === option.id
                      ? 'border-accent bg-accent/10'
                      : 'border-ink/15 bg-surface hover:border-accent/50',
                  ].join(' ')}
                >
                  <span className="mt-0.5 font-mono text-xs font-bold text-ink/50">
                    {option.id})
                  </span>
                  <span>{option.text}</span>
                </button>
              ))}
            </div>
          ) : view.type === 'free-text' ? (
            <>
              <textarea
                className="input min-h-[16rem] resize-y font-normal leading-relaxed"
                value={value}
                onChange={(event) => registerInput(event.target.value)}
                placeholder="Type your answer here."
                autoFocus
              />
              <div className="mt-2 text-xs">
                <span className={overLimit ? 'font-semibold text-danger' : 'text-ink/50'}>
                  {wordCount} words
                  {view.maxWords !== undefined ? ` (limit ${view.maxWords})` : ''}
                  {overLimit ? ' — over the limit' : ''}
                </span>
              </div>
            </>
          ) : (
            <input
              className="input max-w-xs text-lg"
              value={value}
              onChange={(event) => registerInput(event.target.value)}
              inputMode={view.type === 'numeric' ? 'decimal' : 'text'}
              autoComplete="off"
              autoFocus
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !busy) void submit();
              }}
            />
          )}
        </div>

        {!view.options && (
          <button
            type="button"
            className="btn-primary mt-8"
            onClick={() => void submit()}
            disabled={busy}
          >
            {isEnd ? 'Submit the test' : 'Save and continue'}
          </button>
        )}

        <p className="mt-6 text-xs text-ink/40">
          {section.allowBack
            ? 'You can return to earlier questions in this section.'
            : 'You cannot return to this question after moving on.'}
        </p>
      </main>
    </div>
  );
}
